import { queryKeys } from '@terminal/routes/query-params';
import { useDocumentSourcesTableStore } from '@terminal/views/alfred/document-search/document-sources-view/document-sources-table/use-document-sources-table-store/useDocumentSourcesTableStore';
import {
  ContextOverviewData,
  DocumentMessage,
  DocumentOverviewData,
  DocumentResponse,
} from '@terminal/views/alfred/document-search/use-document-search/use-document-search.types';

const getContextCounts = (documentOverview?: DocumentOverviewData) => ({
  documentsCount: documentOverview?.documents ?? 0,
  companiesCount: documentOverview?.companies ?? 0,
});

const getStatistics = (contextOverview?: ContextOverviewData) => {
  if (!contextOverview) {
    return undefined;
  }

  const {
    country: {
      document_count: countryDocCounts,
      total_count: countryTotalCount,
    },
    company: {
      document_count: companyDocCounts,
      total_count: companyTotalCount,
    },
  } = contextOverview;

  if (!countryTotalCount && !companyTotalCount) {
    return undefined;
  }

  const defaultStatistics = {
    companies: companyTotalCount,
    countries: countryTotalCount,
  };

  return {
    company: {
      documents: companyDocCounts,
      citations: companyDocCounts,
      ...defaultStatistics,
    },
    country: {
      documents: countryDocCounts,
      citations: countryDocCounts,
      ...defaultStatistics,
    },
  };
};

export const useGetDocumentContextCounts = ({
  params,
  messages,
}: {
  params: URLSearchParams;
  messages: DocumentMessage[];
}) => {
  const customContextCounts = useDocumentSourcesTableStore(
    state => state.customContextCounts
  );

  if (customContextCounts) {
    return {
      documentsCount: customContextCounts.documentsCount,
      companiesCount: customContextCounts.companiesCount,
      statistics: customContextCounts.statistics,
    };
  }

  const citationGroupId = params.get(queryKeys.citationGroupId);
  const message = [...messages].reverse().find(message => {
    if (citationGroupId) {
      const primaryReplyData = message.reply?.[0]?.data;
      const overview = primaryReplyData?.overview;
      return (
        primaryReplyData?.citation_group_id === citationGroupId &&
        getContextCounts(overview).documentsCount > 0
      );
    } else {
      return message.status === 'complete';
    }
  });

  const documentReplies = message?.reply as DocumentResponse[] | undefined;
  const docsOverview = documentReplies?.[0]?.data.overview;
  const contextOverview = documentReplies?.[0]?.data.context_overview;
  const { documentsCount, companiesCount } = getContextCounts(docsOverview);
  const contextOverviewStatistics = getStatistics(contextOverview);

  if (contextOverviewStatistics) {
    return {
      documentsCount,
      companiesCount,
      statistics: contextOverviewStatistics,
    };
  }

  return {
    documentsCount,
    companiesCount,
  };
};
